import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../shadcn/components/ui/Card';
import { Button } from '../../../shadcn/components/ui/Button';
import { Input } from '../../../shadcn/components/ui/Input';
import { Separator } from '../../../shadcn/components/ui/Separator';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../shadcn/components/ui/Table';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronsLeftIcon,
    ChevronsRightIcon,
    RefreshCwIcon,
    SearchIcon,
    ChevronUpIcon,
    ChevronDownIcon
} from 'lucide-react';
import { opsVisibilityApi as api } from '../../../api/opsVisibility';

function ViewOutOfBoxAvailability() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.getOutOfBoxAvailability();

            if (response.status) {
                // Handle nested data structure: response.data.data contains the actual array
                setData(response.data?.data || []);
            } else {
                setError('Failed to fetch data');
            }
        } catch (err) {
            setError('An error occurred while fetching data');
            console.error('Error fetching out of box availability data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Filter data based on search term
    const filteredData = useMemo(() => {
        return (Array.isArray(data) ? data : []).filter(item =>
            item.zip_code?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.external_sku?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.line_haul_id?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [data, searchTerm]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    // Paginate data
    const paginatedData = React.useMemo(() => {
        const startIndex = currentPage * pageSize;
        return sortedData.slice(startIndex, startIndex + pageSize);
    }, [sortedData, currentPage, pageSize]);

    // Pagination helpers
    const totalPages = Math.ceil(sortedData.length / pageSize);
    const canPreviousPage = currentPage > 0;
    const canNextPage = currentPage < totalPages - 1;

    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) return null;
        return sortConfig.direction === 'asc' ?
            <ChevronUpIcon className="h-4 w-4" /> :
            <ChevronDownIcon className="h-4 w-4" />;
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-12">
                    <RefreshCwIcon className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading data...</span>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                    <p className="text-red-600 mb-4">{error}</p>
                    <Button onClick={fetchData} variant="outline">
                        <RefreshCwIcon className="h-4 w-4 mr-2" />
                        Retry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle>Out of Box Availability Data</CardTitle>
                        <p className="text-sm text-gray-600 mt-1">
                            {sortedData.length} items total
                        </p>
                    </div>
                    <Button onClick={fetchData} variant="outline" size="sm">
                        <RefreshCwIcon className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                </div>

                <div className="flex items-center space-x-2">
                    <div className="relative flex-1 max-w-sm">
                        <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                        <Input
                            placeholder="Search by zip code, SKU, or line haul ID..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(0); // Reset to first page when searching
                            }}
                            className="pl-8"
                        />
                    </div>
                </div>
            </CardHeader>

            <CardContent>
                <div className="rounded-md border">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50">
                                    <TableHead
                                        className="w-20 px-4 py-3 text-left text-sm font-medium text-gray-900 cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('id')}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span>ID</span>
                                            {getSortIcon('id')}
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="px-4 py-3 text-left text-sm font-medium text-gray-900 cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('zip_code')}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span>Zip Code</span>
                                            {getSortIcon('zip_code')}
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="px-4 py-3 text-left text-sm font-medium text-gray-900 cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('external_sku')}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span>External SKU</span>
                                            {getSortIcon('external_sku')}
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="px-4 py-3 text-left text-sm font-medium text-gray-900 cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('line_haul_id')}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span>Line Haul ID</span>
                                            {getSortIcon('line_haul_id')}
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="px-4 py-3 text-left text-sm font-medium text-gray-900 cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('created_at')}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span>Created At</span>
                                            {getSortIcon('created_at')}
                                        </div>
                                    </TableHead>
                                    <TableHead
                                        className="px-4 py-3 text-left text-sm font-medium text-gray-900 cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('updated_at')}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span>Updated At</span>
                                            {getSortIcon('updated_at')}
                                        </div>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((item) => (
                                        <TableRow key={item.id} className="border-b hover:bg-gray-50">
                                            <TableCell className="px-4 py-3 text-sm">
                                                <span className="font-mono text-sm">{item.id}</span>
                                            </TableCell>
                                            <TableCell className="px-4 py-3 text-sm">
                                                <span className="font-mono text-sm">{item.zip_code}</span>
                                            </TableCell>
                                            <TableCell className="px-4 py-3 text-sm">
                                                <span className="font-mono text-sm">{item.external_sku}</span>
                                            </TableCell>
                                            <TableCell className="px-4 py-3 text-sm">
                                                <span className="font-mono text-sm">{item.line_haul_id}</span>
                                            </TableCell>
                                            <TableCell className="px-4 py-3 text-sm">
                                                {item.created_at ? new Date(item.created_at).toLocaleString() : 'N/A'}
                                            </TableCell>
                                            <TableCell className="px-4 py-3 text-sm">
                                                {item.updated_at ? new Date(item.updated_at).toLocaleString() : 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                            {searchTerm ? "No items match your search" : "No data found"}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                <Separator className="my-4" />

                {/* Pagination */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <p className="text-sm text-gray-700">
                            Showing {currentPage * pageSize + 1} to{' '}
                            {Math.min((currentPage + 1) * pageSize, sortedData.length)}{' '}
                            of {sortedData.length} results
                        </p>
                    </div>

                    <div className="flex items-center space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(0)}
                            disabled={!canPreviousPage}
                        >
                            <ChevronsLeftIcon className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            disabled={!canPreviousPage}
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </Button>
                        <span className="text-sm">
                            Page {currentPage + 1} of {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            disabled={!canNextPage}
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(totalPages - 1)}
                            disabled={!canNextPage}
                        >
                            <ChevronsRightIcon className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export { ViewOutOfBoxAvailability }; 